import { Combobox } from "design-system/organisms/Combobox"
import { useGetBackofficeCompaniesQuery } from "modules/backoffice/hooks/companies/useGetBackofficeCompaniesQuery"
import { useDebouncedValue } from "modules/shared/hooks/useDebouncedValue"
import { ReactElement, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { OrganizationTypeEnum } from "schema"

type Option = {
  value: string
  label: string | ReactElement
  muted?: boolean
}

type CompanySelectorProps = {
  value: string | undefined
  onChange: (companyId: string | undefined) => void
  className?: string
  hasAmphoraIntegration?: boolean
  hasShopifyIntegration?: boolean
  organizationTypes?: Array<(typeof OrganizationTypeEnum)[keyof typeof OrganizationTypeEnum]>
  showAllCompaniesOption?: boolean
  allCompaniesLabel?: string
  placeholder?: string
  emptyPlaceholder?: string
  searchPlaceholder?: string
}

export const CompanySelector = ({
  value,
  onChange,
  className,
  hasAmphoraIntegration = false,
  hasShopifyIntegration = false,
  organizationTypes = [OrganizationTypeEnum.Client, OrganizationTypeEnum.Partner],
  showAllCompaniesOption = true,
  allCompaniesLabel,
  placeholder,
  emptyPlaceholder,
  searchPlaceholder,
}: CompanySelectorProps) => {
  const { t } = useTranslation()
  const [searchQuery, setSearchQuery] = useState("")
  const debouncedSearchQuery = useDebouncedValue({
    value: searchQuery,
  })

  const { data: companies, isPending } = useGetBackofficeCompaniesQuery({
    query: {
      query: debouncedSearchQuery || undefined,
      page: 1,
      total: 100,
      sortBy: { field: "name", order: "asc" },
      filterBy: {
        type: organizationTypes,
        hasAmphoraIntegration,
        hasShopifyIntegration,
      },
    },
  })

  const options = useMemo(() => {
    const array: Option[] = []

    if (showAllCompaniesOption) {
      array.push({
        label: allCompaniesLabel ?? t("backoffice.bi.allCompanies"),
        value: "all",
        muted: true,
      })
    }

    if (companies) {
      array.push(
        ...companies.map((company) => ({
          label: company.name,
          value: company.id,
        })),
      )
    }

    return array
  }, [allCompaniesLabel, companies, showAllCompaniesOption, t])

  const selectedOption = useMemo(() => {
    if (!value || value === "all") {
      return showAllCompaniesOption ? options[0] : undefined
    }
    return options.find((option) => option.value === value)
  }, [value, options, showAllCompaniesOption])

  function onComboboxChange(option: Option | undefined) {
    const newValue = option?.value === "all" ? undefined : option?.value
    onChange(newValue)
  }

  return (
    <Combobox
      options={options}
      value={selectedOption}
      onChange={onComboboxChange}
      placeholder={placeholder ?? t("backoffice.bi.selectCompany")}
      emptyPlaceholder={emptyPlaceholder ?? t("backoffice.bi.noCompaniesFound")}
      searchPlaceholder={searchPlaceholder ?? t("backoffice.bi.searchCompany")}
      disabled={isPending}
      searchQuery={searchQuery}
      setSearchQuery={setSearchQuery}
      className={className}
      shouldFilter={false}
    />
  )
}
